/**
 * "Me voy sin internet N horas": decide solo que llevarte.
 *
 * La idea es no tener que ir serie por serie marcando capitulos. Se parte de
 * lo que estas siguiendo de verdad —las series por las que has pasado hace
 * menos— y se reparten los capitulos pendientes entre ellas.
 */

import { obtenerNoLeidos, obtenerSeriesPendientes, type SeriePendiente } from '../api/consultas'
import { leerFinalizada } from './finalizadas'
import { estaDescargado, listarDescargados } from './offline'

/**
 * Un capitulo se lee en poco menos de diez minutos. Es un promedio flojo
 * —un manhwa largo se va al doble— pero sirve para traducir horas a
 * capitulos, que es lo unico que se le pide.
 */
const CAPITULOS_POR_HORA = 7

/** Cuantos capitulos seguidos merece cada serie antes de abrir otra. */
const MINIMO_POR_SERIE = 4

/** Solo para la primera vez, cuando no hay descargas de las que aprender. */
const BYTES_SUPUESTOS = 6 * 1024 * 1024

export interface CapituloPlan {
  id: number
  nombre: string
  numero: number
  mangaId: number
  mangaTitulo: string
  portada: string | null
}

export interface GrupoPlan {
  mangaId: number
  titulo: string
  portada: string | null
  capitulos: CapituloPlan[]
}

export interface Plan {
  capitulos: CapituloPlan[]
  grupos: GrupoPlan[]
  bytesEstimados: number
  /** Series con pendientes que no cupieron, para poder decirlo. */
  seriesFuera: number
}

/**
 * Cuanto ocupa un capitulo, aprendido de lo que ya te has bajado.
 *
 * No se puede calcular de antemano: un capitulo sin abrir tiene `pageCount`
 * a -1 porque el servidor aun no ha resuelto sus paginas. Asi que se mira el
 * historial propio, que ademas se ajusta al tipo de series de cada uno: un
 * manhwa a tira ocupa mucho mas que un manga normal.
 */
export async function bytesPorCapitulo(): Promise<number> {
  const descargados = await listarDescargados()
  if (descargados.length === 0) return BYTES_SUPUESTOS
  const total = descargados.reduce((suma, c) => suma + c.bytes, 0)
  return Math.round(total / descargados.length)
}

function leidoEn(serie: SeriePendiente): number {
  return Number(serie.lastReadChapter?.lastReadAt ?? 0) || 0
}

export async function planificar(horas: number): Promise<Plan> {
  const objetivo = Math.max(1, Math.round(horas * CAPITULOS_POR_HORA))

  const conPendientes = (await obtenerSeriesPendientes())
    // Una serie finalizada no se lleva de viaje aunque le queden capitulos
    // sin leer: eso es justo lo que pasa con las que se dejaron a medias.
    .filter((serie) => serie.unreadCount > 0 && !leerFinalizada(serie.meta))
    // Las nunca empezadas caen al final: leidoEn devuelve 0
    .sort((a, b) => leidoEn(b) - leidoEn(a))

  // Repartir entre todas dejaria un capitulo suelto por serie, que no es un
  // viaje sino una degustacion. Se limita el numero de series para que a
  // cada una le toque una tirada decente.
  const cabenSeries = Math.max(1, Math.ceil(objetivo / MINIMO_POR_SERIE))
  const elegidas = conPendientes.slice(0, cabenSeries)

  const listas = await Promise.all(
    elegidas.map(async (serie) => {
      const pendientes = await obtenerNoLeidos(serie.id)
      const candidatos: CapituloPlan[] = []
      for (const capitulo of pendientes) {
        // Lo que ya esta en el movil no cuenta ni ocupa
        if (await estaDescargado(capitulo.id)) continue
        candidatos.push({
          id: capitulo.id,
          nombre: capitulo.name,
          numero: capitulo.chapterNumber,
          mangaId: serie.id,
          mangaTitulo: serie.title,
          portada: serie.thumbnailUrl,
        })
      }
      return candidatos
    }),
  )

  // Reparto por turnos: uno de cada serie por vuelta, en orden de recencia.
  // Asi no te llevas veinte capitulos de una y ninguno de las demas.
  const capitulos: CapituloPlan[] = []
  for (let vuelta = 0; capitulos.length < objetivo; vuelta++) {
    let quedaAlgo = false
    for (const lista of listas) {
      if (capitulos.length >= objetivo) break
      const capitulo = lista[vuelta]
      if (!capitulo) continue
      capitulos.push(capitulo)
      quedaAlgo = true
    }
    if (!quedaAlgo) break
  }

  const grupos: GrupoPlan[] = []
  for (const capitulo of capitulos) {
    let grupo = grupos.find((g) => g.mangaId === capitulo.mangaId)
    if (!grupo) {
      grupo = {
        mangaId: capitulo.mangaId,
        titulo: capitulo.mangaTitulo,
        portada: capitulo.portada,
        capitulos: [],
      }
      grupos.push(grupo)
    }
    grupo.capitulos.push(capitulo)
  }

  return {
    capitulos,
    grupos,
    bytesEstimados: capitulos.length * (await bytesPorCapitulo()),
    seriesFuera: Math.max(0, conPendientes.length - grupos.length),
  }
}
